import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatContactType } from "@/lib/format-contact-type";

export const CONTACT_QUICK_PICK_LIMIT = 8;

export interface ClaimQuickPickContact {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  contactType?: string | null;
  entityType?: string | null;
  companyName?: string | null;
}

export type QuickPickField = "to" | "cc";

interface ContactQuickPicksProps {
  contacts: ClaimQuickPickContact[];
  selectedEmails: string[];
  occupiedEmails?: string[];
  field: QuickPickField;
  onToggle: (email: string) => void;
}

export function contactDisplayName(contact: ClaimQuickPickContact): string {
  const person = [contact.firstName, contact.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  if (person) return person;

  const company = (contact.companyName || "").trim();
  if (company) return company;

  return (contact.email || "").trim();
}

export function contactQuickPickLabel(contact: ClaimQuickPickContact): string {
  const name = contactDisplayName(contact);
  const role = contact.contactType ? formatContactType(contact.contactType) : "";
  if (name && role) return `${name} (${role})`;
  return name || role;
}

export function normalizeEmail(email: string | null | undefined): string {
  return (email || "").trim().toLowerCase();
}

export function dedupeClaimContacts(
  contacts: ClaimQuickPickContact[],
): ClaimQuickPickContact[] {
  const seenEmails = new Set<string>();
  const result: ClaimQuickPickContact[] = [];

  for (const contact of contacts) {
    const emailKey = normalizeEmail(contact.email);
    if (emailKey) {
      if (seenEmails.has(emailKey)) continue;
      seenEmails.add(emailKey);
    }
    result.push(contact);
  }

  return result;
}

function hasEmail(contact: ClaimQuickPickContact): boolean {
  return Boolean(contact.email?.trim());
}

function emailSet(emails: string[]): Set<string> {
  return new Set(emails.map((email) => normalizeEmail(email)).filter(Boolean));
}

export function ContactQuickPicks({
  contacts,
  selectedEmails,
  occupiedEmails = [],
  field,
  onToggle,
}: ContactQuickPicksProps) {
  const unique = dedupeClaimContacts(contacts);
  if (unique.length === 0) return null;

  // Stable sort: usable emails first so the 8-button cap is not filled by
  // disabled "No email on file" contacts (server order is not guaranteed).
  const prioritized = unique.slice().sort((a, b) => {
    const aRank = hasEmail(a) ? 0 : 1;
    const bRank = hasEmail(b) ? 0 : 1;
    return aRank - bRank;
  });
  const visible = prioritized.slice(0, CONTACT_QUICK_PICK_LIMIT);
  const overflow = prioritized.length - visible.length;
  const selected = emailSet(selectedEmails);
  const occupied = emailSet(occupiedEmails);
  const occupiedReason = field === "to" ? "Already in CC" : "Already in To";

  return (
    <div
      className="flex flex-wrap gap-1.5 mt-2"
      data-testid={`contact-quick-picks-${field}`}
    >
      {visible.map((contact) => {
        const email = contact.email?.trim() ?? "";
        const emailKey = normalizeEmail(email);
        const label = contactQuickPickLabel(contact);
        const isSelected = Boolean(emailKey && selected.has(emailKey));
        const isOccupied = Boolean(emailKey && occupied.has(emailKey));
        const missingEmail = !hasEmail(contact);
        const disabled = missingEmail || (isOccupied && !isSelected);
        const showOccupiedReason = isOccupied && !isSelected;
        const tooltip = missingEmail
          ? "No email on file"
          : showOccupiedReason
            ? occupiedReason
            : email;

        return (
          <Tooltip key={`${field}-${contact.id}`}>
            <TooltipTrigger asChild>
              <span className="inline-flex max-w-full">
                <Button
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  disabled={disabled}
                  aria-pressed={isSelected}
                  aria-label={
                    disabled
                      ? `${label}. ${tooltip}`
                      : `${isSelected ? "Remove" : "Add"} ${label}`
                  }
                  data-testid={`contact-quick-pick-${field}-${contact.id}`}
                  className={cn(
                    "min-h-9 h-9 max-w-full",
                    isSelected
                      ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90 shadow-none"
                      : "bg-background text-foreground border-input",
                    disabled && "opacity-50",
                  )}
                  onClick={() => {
                    if (disabled || !email) return;
                    onToggle(email);
                  }}
                >
                  {isSelected && (
                    <Check
                      className="h-3.5 w-3.5 shrink-0"
                      data-testid={`contact-quick-pick-check-${field}-${contact.id}`}
                      aria-hidden="true"
                    />
                  )}
                  <span className="truncate">{label}</span>
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <span
          className="inline-flex items-center min-h-9 px-2 text-xs text-muted-foreground"
          data-testid={`contact-quick-picks-overflow-${field}`}
        >
          +{overflow} more
        </span>
      )}
    </div>
  );
}
